import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { CountryRoute } from '../../components/CountryDisplay';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../components/ui/collapsible';
import { ChevronDown, ChevronUp, TrendingUp, AlertCircle, Save, RefreshCw, Ship, Package } from 'lucide-react';
import { DataTable } from '../../components/ui/data-table';
import EmptyState from '../../components/EmptyState';
import {
  fmt,
  CHARGE_LABELS,
  SETTINGS_CHARGES,
  PAYMENT_STATUS_LABELS,
  SummaryCard,
} from './shared';

// ─────────────────────────────────────────────────────────────────────────────
// ShipmentFinanceDialog
// ─────────────────────────────────────────────────────────────────────────────
const ShipmentFinanceDialog = ({ shipment, onClose, onSaved }) => {
  const { t, language } = useLanguage();
  const chargeLabels = (CHARGE_LABELS[language] || CHARGE_LABELS.fr);
  const paymentLabels = (PAYMENT_STATUS_LABELS[language] || PAYMENT_STATUS_LABELS.fr);
  const [finance, setFinance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({});
  const [showCharges, setShowCharges] = useState(false);

  const fetchFinance = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/api/finance/shipments/${shipment.id}`);
      setFinance(res.data);
      const initial = {};
      Object.keys(chargeLabels).forEach((k) => {
        initial[k] = res.data[k] !== undefined ? String(res.data[k]) : '';
      });
      initial.notes = res.data.notes || '';
      setForm(initial);
    } catch (e) {
      setError(t('financeLoadError'));
    } finally {
      setLoading(false);
    }
  }, [shipment.id, t]);

  useEffect(() => {
    fetchFinance();
  }, [fetchFinance]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = {};
      Object.keys(chargeLabels).forEach((k) => {
        payload[k] = parseFloat(form[k]) || 0;
      });
      payload.notes = form.notes || '';
      const res = await api.post(`/api/finance/shipments/${shipment.id}`, payload);
      setFinance(res.data);
      setSuccess(t('financeSaveSuccess'));
      onSaved && onSaved(res.data);
    } catch (e) {
      setError(t('saveError'));
    } finally {
      setSaving(false);
    }
  };

  const totalKg = finance ? finance.prix_total_kg_compagnie : null;
  const packages = finance?.packages || [];

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TrendingUp size={20} />
            {t('financeDetail')} — {shipment.name || shipment.reference || shipment.id}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8 text-muted-foreground">{t('loading')}</div>
        ) : (
          <div className="space-y-6">
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
                <AlertCircle size={16} />
                {error}
              </div>
            )}
            {success && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-md text-green-700 text-sm">
                {success}
              </div>
            )}

            {finance?.shipment && (
              <div className="bg-muted rounded-lg p-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                  <div>
                    <span className="text-muted-foreground text-xs block">{t('shipment')}</span>
                    <span className="font-medium text-foreground">{finance.shipment.name}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground text-xs block">{t('route')}</span>
                    <span className="font-medium text-foreground">
                      <CountryRoute origin={finance.shipment.origin_country} destination={finance.shipment.destination_country} size="sm" />
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground text-xs block">{t('departureDate')}</span>
                    <span className="font-medium text-foreground">
                      {finance.shipment.departure_date ? new Date(finance.shipment.departure_date).toLocaleDateString(language === 'en' ? 'en-GB' : 'fr-FR') : '—'}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground text-xs block">{t('totalWeight')}</span>
                    <span className="font-medium text-foreground">{(finance.poids_total || 0).toFixed(1)} kg</span>
                  </div>
                </div>
              </div>
            )}

            {finance && (
              <div>
                <h3 className="font-semibold text-foreground mb-3 text-sm uppercase tracking-wide">
                  {t('financeSummary')}
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <SummaryCard label={t('totalRevenue')} value={fmt(finance.total_revenue)} />
                  <SummaryCard label={t('collectedRevenue')} value={fmt(finance.total_paid)} />
                  <SummaryCard
                    label={t('debtUnpaid')}
                    value={fmt(finance.total_debt)}
                    highlight={finance.total_debt > 0 ? 'warning' : 'ok'}
                  />
                  <SummaryCard label={t('totalCharges')} value={fmt(finance.total_charges)} />
                  <SummaryCard
                    label={t('profit')}
                    value={fmt(finance.profit)}
                    highlight={finance.profit >= 0 ? 'profit' : 'loss'}
                    span2
                  />
                </div>
              </div>
            )}

            {packages.length > 0 && (
              <div>
                <h3 className="font-semibold text-foreground mb-3 text-sm uppercase tracking-wide flex items-center gap-2">
                  <Package size={16} />
                  {t('relatedPackages')} ({packages.length})
                </h3>
                <div className="border border-border rounded-lg overflow-x-auto">
                  <table className="analytics-table w-full min-w-[500px]">
                    <thead>
                      <tr>
                        <th>{t('oceCode')}</th>
                        <th>{t('sender')}</th>
                        <th>{t('recipient')}</th>
                        <th style={{ textAlign: 'right' }}>{t('weight')}</th>
                        <th style={{ textAlign: 'right' }}>{t('price')}</th>
                        <th style={{ textAlign: 'center' }}>{t('payment')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {packages.map((pkg) => {
                        const ps = paymentLabels[pkg.payment_status] || paymentLabels.unpaid;
                        return (
                          <tr key={pkg.id}>
                            <td className="font-mono text-xs">
                              <button
                                type="button"
                                onClick={() => navigate(`/finance/invoices?search=${encodeURIComponent(pkg.oce_code || pkg.id?.slice(-8))}`)}
                                className="text-accent hover:underline cursor-pointer"
                              >
                                {pkg.oce_code || pkg.id?.slice(-8)}
                              </button>
                            </td>
                            <td>{pkg.sender_name || '—'}</td>
                            <td>{pkg.recipient_name || '—'}</td>
                            <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                              {pkg.weight ? `${pkg.weight.toFixed(1)} kg` : '—'}
                            </td>
                            <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                              {pkg.price != null ? fmt(pkg.price) : '—'}
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <Badge variant="outline" className={`text-[10px] ${ps.cls}`}>
                                {ps.label}
                              </Badge>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-border">
                        <td colSpan={3} className="font-semibold text-foreground">{t('total')}</td>
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }} className="font-semibold">
                          {(finance?.poids_total || 0).toFixed(1)} kg
                        </td>
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }} className="font-semibold">
                          {fmt(finance?.total_revenue)}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <span className="text-xs text-muted-foreground">
                            {packages.filter(p => p.is_paid).length}/{packages.length} {t('paid')}
                          </span>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}

            <Collapsible open={showCharges} onOpenChange={setShowCharges}>
              <CollapsibleTrigger asChild>
                <Button variant="outline" size="sm" className="w-full flex items-center justify-between">
                  <span className="font-medium text-sm">{t('chargesDetail')}</span>
                  {showCharges ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {Object.entries(chargeLabels).map(([field, label]) => {
                    if (field === 'tarif_kg_compagnie') {
                      return (
                        <div key={field} className="space-y-1">
                          <Label className="text-xs text-muted-foreground">{label}</Label>
                          <div className="flex gap-2 items-center">
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={form[field] || ''}
                              onChange={(e) => handleChange(field, e.target.value)}
                              className="text-sm"
                              placeholder="0.00"
                            />
                            {totalKg !== null && (
                              <span className="text-xs text-muted-foreground whitespace-nowrap bg-muted px-2 py-1 rounded">
                                = {fmt(totalKg)}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    }
                    return (
                      <div key={field} className="space-y-1">
                        <Label className="text-xs text-muted-foreground">{label}</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={form[field] || ''}
                          onChange={(e) => handleChange(field, e.target.value)}
                          className="text-sm"
                          placeholder="0.00"
                        />
                      </div>
                    );
                  })}
                </div>

                <div className="mt-3 space-y-1">
                  <Label className="text-xs text-muted-foreground">{t('notes')}</Label>
                  <textarea
                    value={form.notes || ''}
                    onChange={(e) => handleChange('notes', e.target.value)}
                    className="w-full border border-input rounded-md px-3 py-2 text-sm resize-none h-16 focus:outline-none focus:ring-2 focus:ring-ring"
                    placeholder={t('optionalRemarks')}
                  />
                </div>
              </CollapsibleContent>
            </Collapsible>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={onClose}>
                {t('close')}
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                <Save size={16} className="mr-1" />
                {saving ? t('saving') : t('save')}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// DefaultSettingsForm
// ─────────────────────────────────────────────────────────────────────────────
const DefaultSettingsForm = () => {
  const { t, language } = useLanguage();
  const chargeLabels = (CHARGE_LABELS[language] || CHARGE_LABELS.fr);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [open, setOpen] = useState(false);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/finance/settings');
      const initial = {};
      SETTINGS_CHARGES.forEach((k) => {
        initial[k] = res.data[k] !== undefined ? String(res.data[k]) : '';
      });
      setSettings(initial);
    } catch {
      setError(t('settingsLoadError'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = {};
      SETTINGS_CHARGES.forEach((k) => {
        payload[k] = parseFloat(settings[k]) || 0;
      });
      await api.put('/api/finance/settings', payload);
      setSuccess(t('settingsUpdated'));
    } catch {
      setError(t('saveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="mt-6">
      <CollapsibleTrigger asChild>
        <Button variant="outline" className="w-full flex items-center justify-between">
          <span className="font-medium">{t('defaultChargesSettings')}</span>
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <Card className="mt-2 border-border">
          <CardContent className="pt-4">
            {loading ? (
              <div className="text-sm text-muted-foreground">{t('loading')}</div>
            ) : (
              <div className="space-y-4">
                {error && (
                  <div className="text-red-600 text-sm">{error}</div>
                )}
                {success && (
                  <div className="text-green-600 text-sm">{success}</div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {SETTINGS_CHARGES.map((field) => (
                    <div key={field} className="space-y-1">
                      <Label className="text-xs text-muted-foreground">{chargeLabels[field]}</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={settings[field] || ''}
                        onChange={(e) =>
                          setSettings((prev) => ({ ...prev, [field]: e.target.value }))
                        }
                        className="text-sm"
                        placeholder="0.00"
                      />
                    </div>
                  ))}
                </div>
                <div className="flex justify-end">
                  <Button onClick={handleSave} disabled={saving} size="sm">
                    <Save size={14} className="mr-1" />
                    {saving ? t('saving') : t('saveDefaults')}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </CollapsibleContent>
    </Collapsible>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// FinanceShipmentsPage
// ─────────────────────────────────────────────────────────────────────────────
const FinanceShipmentsPage = () => {
  const { onSummaryLoaded } = useOutletContext();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);

  const fetchShipments = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/api/finance/shipments');
      setShipments(res.data.shipments || []);
      if (res.data.summary && onSummaryLoaded) onSummaryLoaded(res.data.summary);
    } catch {
      setError(t('shipmentsLoadError'));
    } finally {
      setLoading(false);
    }
  }, [onSummaryLoaded]);

  useEffect(() => {
    fetchShipments();
  }, [fetchShipments]);

  const handleSaved = () => {
    fetchShipments();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground">{t('shipments')}</h2>
        <Button variant="outline" size="sm" onClick={fetchShipments}>
          <RefreshCw size={14} className="mr-1" />
          {t('refresh')}
        </Button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">{t('loading')}</div>
      ) : shipments.length === 0 ? (
        <EmptyState
          icon={Ship}
          title={t('noShipments')}
          description={t('noShipmentsDescription')}
        />
      ) : (
        <DataTable
          data={shipments}
          searchPlaceholder={t('searchShipmentRoute')}
          emptyMessage={t('noShipments')}
          columns={[
            {
              accessorKey: 'name',
              header: t('shipment'),
              cell: ({ row }) => {
                const s = row.original;
                return (
                  <div>
                    <div className="font-medium text-foreground">{s.name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5"><CountryRoute origin={s.origin_country} destination={s.destination_country} size="sm" /></div>
                  </div>
                );
              },
            },
            {
              accessorFn: (row) => row.departure_date ? new Date(row.departure_date).getTime() : 0,
              id: 'departure_date',
              header: t('departureDate'),
              cell: ({ row }) => (
                <span className="text-muted-foreground text-xs whitespace-nowrap">
                  {row.original.departure_date ? new Date(row.original.departure_date).toLocaleDateString(language === 'en' ? 'en-GB' : 'fr-FR') : '—'}
                </span>
              ),
              meta: { className: 'text-right', cellClassName: 'text-right' },
            },
            {
              accessorKey: 'package_count',
              header: t('packages'),
              meta: { className: 'text-right', cellClassName: 'text-right text-foreground' },
            },
            {
              accessorFn: (row) => row.finance?.poids_total ?? 0,
              id: 'poids',
              header: t('weight'),
              cell: ({ getValue }) => {
                const v = getValue();
                return <span className="text-foreground">{v ? `${v.toFixed(1)} kg` : '—'}</span>;
              },
              meta: { className: 'text-right', cellClassName: 'text-right' },
            },
            {
              accessorFn: (row) => row.finance?.total_revenue ?? 0,
              id: 'revenue',
              header: t('revenue'),
              cell: ({ getValue }) => <span className="text-foreground">{fmt(getValue())}</span>,
              meta: { className: 'text-right', cellClassName: 'text-right' },
            },
            {
              accessorFn: (row) => row.finance?.total_charges ?? 0,
              id: 'charges',
              header: t('charges'),
              cell: ({ getValue }) => <span className="text-foreground">{fmt(getValue())}</span>,
              meta: { className: 'text-right', cellClassName: 'text-right' },
            },
            {
              accessorFn: (row) => row.finance?.profit ?? 0,
              id: 'profit',
              header: t('profit'),
              cell: ({ getValue }) => {
                const v = getValue();
                return <span className={`font-semibold ${v >= 0 ? 'text-green-600' : 'text-red-600'}`}>{fmt(v)}</span>;
              },
              meta: { className: 'text-right', cellClassName: 'text-right' },
            },
            {
              accessorFn: (row) => row.finance?.margin_pct ?? 0,
              id: 'margin',
              header: t('marginPercent'),
              cell: ({ getValue }) => {
                const v = getValue();
                const color = v >= 20 ? 'text-green-600' : v > 0 ? 'text-amber-600' : 'text-red-600';
                return <span className={`font-medium ${color}`}>{v !== undefined ? `${v}%` : '—'}</span>;
              },
              meta: { className: 'text-right', cellClassName: 'text-right' },
            },
            {
              accessorFn: (row) => row.finance?.is_profitable ? 1 : 0,
              id: 'status',
              header: t('status'),
              cell: ({ row }) => {
                const profitable = row.original.finance?.is_profitable;
                return (
                  <Badge
                    variant="outline"
                    className={profitable ? 'bg-green-100 text-green-700 border-green-200' : 'bg-red-100 text-red-700 border-red-200'}
                  >
                    {profitable ? t('profitable') : t('unprofitable')}
                  </Badge>
                );
              },
              meta: { className: 'text-center', cellClassName: 'text-center' },
              enableSorting: false,
            },
            {
              id: 'actions',
              header: '',
              cell: ({ row }) => (
                <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); setSelected(row.original); }}>
                  {t('details')}
                </Button>
              ),
              meta: { cellClassName: 'text-right' },
              enableSorting: false,
            },
          ]}
        />
      )}

      <DefaultSettingsForm />

      {selected && (
        <ShipmentFinanceDialog
          shipment={selected}
          onClose={() => setSelected(null)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
};

export default FinanceShipmentsPage;
